package dev.akshaychordiya.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.CloseIcon
import com.varabyte.kobweb.silk.components.icons.HamburgerIcon
import com.varabyte.kobweb.silk.components.icons.MoonIcon
import com.varabyte.kobweb.silk.components.icons.SunIcon
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.akshaychordiya.components.widgets.IconButton
import dev.akshaychordiya.content.Site
import dev.akshaychordiya.content.navItems
import dev.akshaychordiya.smoothTransition
import dev.akshaychordiya.swapIn
import dev.akshaychordiya.toSitePalette
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span

val NavHeaderStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(64.cssRem)
            .padding(topBottom = 1.25.cssRem, leftRight = 1.5.cssRem)
    }
    Breakpoint.MD { Modifier.padding(topBottom = 1.75.cssRem, leftRight = 2.cssRem) }
}

val WordmarkStyle = CssStyle {
    base {
        Modifier
            .fontSize(1.05.cssRem)
            .fontWeight(600)
            .letterSpacing((-0.02).cssRem)
            .smoothTransition("color")
    }
    hover { Modifier.color(colorMode.toSitePalette().brandPrimary) }
}

/**
 * Nav links grow an underline from the left on hover.
 *
 * Drawn as a pseudo-element that scales on the X axis rather than as a real border, so the text never shifts and the
 * animation runs on the compositor.
 */
val NavLinkStyle = CssStyle {
    base {
        Modifier
            .fontSize(0.95.cssRem)
            .color(colorMode.toSitePalette().subdued)
            .position(Position.Relative)
            .smoothTransition("color")
    }
    hover { Modifier.color(colorMode.toSitePalette().brandPrimary) }

    cssRule("::after") {
        Modifier
            .content("")
            .position(Position.Absolute)
            .left(0.px)
            .bottom((-4).px)
            .width(100.percent)
            .height(1.5.px)
            .backgroundColor(colorMode.toSitePalette().brandPrimary)
            .styleModifier {
                property("transform", "scaleX(0)")
                property("transform-origin", "left")
                property("transition", "transform 220ms cubic-bezier(0.16, 1, 0.3, 1)")
            }
    }
    cssRule(":hover::after") {
        Modifier.styleModifier { property("transform", "scaleX(1)") }
    }
}

/** Full colour and a permanent underline, so the current section reads as current rather than merely hovered. */
val NavLinkActiveStyle = CssStyle {
    base { Modifier.color(colorMode.toSitePalette().brandPrimary).fontWeight(600) }
    cssRule("::after") { Modifier.styleModifier { property("transform", "scaleX(1)") } }
}

/**
 * Whether [path] is the section currently being viewed.
 *
 * An article lives at `/articles/<slug>`, so "Articles" should stay lit while you're reading one - hence the prefix
 * match rather than equality. `/` is excluded because it prefixes everything.
 */
private fun isActive(currentPath: String, path: String): Boolean =
    currentPath == path || (path != "/" && currentPath.startsWith("$path/"))

@Composable
private fun NavLink(path: String, text: String, active: Boolean) {
    Link(
        path,
        text,
        NavLinkStyle.toModifier().thenIf(active) { NavLinkActiveStyle.toModifier() },
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    )
}

@Composable
private fun MenuItems() {
    // Trailing slashes vary by how you arrived, so normalise before comparing.
    val currentPath = rememberPageContext().route.path.removeSuffix("/").ifEmpty { "/" }
    // "Home" is reachable via the wordmark, so it would only be noise in the link row.
    navItems.filter { it.path != "/" }.forEach {
        NavLink(it.path, it.label, active = isActive(currentPath, it.path))
    }
}

@Composable
private fun ColorModeButton() {
    var colorMode by ColorMode.currentState
    IconButton(
        onClick = { colorMode = colorMode.opposite },
        ariaLabel = if (colorMode.isLight) "Switch to dark mode" else "Switch to light mode",
    ) {
        // key() so the span is a genuinely new DOM node on each toggle - swapIn would otherwise never replay, since
        // an animation doesn't restart just because a node recomposed.
        key(colorMode) {
            Span(
                attrs = Modifier
                    .swapIn()
                    .styleModifier { property("display", "inline-flex") }
                    .toAttrs()
            ) {
                if (colorMode.isLight) MoonIcon() else SunIcon()
            }
        }
    }
}

val SideMenuSlideInAnim = Keyframes {
    from { Modifier.translateX(100.percent) }
    to { Modifier }
}

/**
 * Closing the side menu animates it out rather than unmounting it immediately, so [CLOSING] is a real state we have to
 * render through before we can stop drawing the menu.
 */
enum class SideMenuState {
    CLOSED,
    OPEN,
    CLOSING;

    fun close() = when (this) {
        CLOSED -> CLOSED
        OPEN -> CLOSING
        CLOSING -> CLOSING
    }
}

@Composable
fun NavHeader() {
    Row(NavHeaderStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
        Link("/", variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)) {
            SpanText(Site.NAME, WordmarkStyle.toModifier())
        }

        Spacer()

        Row(
            Modifier.gap(1.75.cssRem).displayIfAtLeast(Breakpoint.MD),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MenuItems()
            ColorModeButton()
        }

        Row(
            Modifier.fontSize(1.4.cssRem).gap(0.5.cssRem).displayUntil(Breakpoint.MD),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }

            ColorModeButton()
            IconButton(onClick = { menuState = SideMenuState.OPEN }, ariaLabel = "Open menu") { HamburgerIcon() }

            if (menuState != SideMenuState.CLOSED) {
                SideMenu(
                    menuState,
                    close = { menuState = menuState.close() },
                    onAnimationEnd = { if (menuState == SideMenuState.CLOSING) menuState = SideMenuState.CLOSED },
                )
            }
        }
    }
}

@Composable
private fun SideMenu(menuState: SideMenuState, close: () -> Unit, onAnimationEnd: () -> Unit) {
    Overlay(
        Modifier
            .setVariable(OverlayVars.BackgroundColor, Colors.Black.copyf(alpha = 0.4f))
            .onClick { close() }
    ) {
        key(menuState) { // Force the animation to recompute when the close button is clicked.
            Column(
                Modifier
                    .fillMaxHeight()
                    .width(clamp(12.cssRem, 60.percent, 16.cssRem))
                    .padding(top = 1.25.cssRem, leftRight = 1.25.cssRem)
                    .gap(1.5.cssRem)
                    .backgroundColor(ColorMode.current.toSitePalette().surface)
                    .align(Alignment.CenterEnd)
                    .animation(
                        SideMenuSlideInAnim.toAnimation(
                            duration = 200.ms,
                            timingFunction = if (menuState == SideMenuState.OPEN) {
                                AnimationTimingFunction.EaseOut
                            } else {
                                AnimationTimingFunction.EaseIn
                            },
                            direction = if (menuState == SideMenuState.OPEN) {
                                AnimationDirection.Normal
                            } else {
                                AnimationDirection.Reverse
                            },
                            fillMode = AnimationFillMode.Forwards,
                        )
                    )
                    .borderRadius(topLeft = 1.25.cssRem)
                    .onClick { it.stopPropagation() }
                    .onAnimationEnd { onAnimationEnd() },
                horizontalAlignment = Alignment.End,
            ) {
                IconButton(onClick = { close() }, ariaLabel = "Close menu") { CloseIcon() }
                Column(
                    Modifier.padding(right = 0.5.cssRem).gap(1.25.cssRem).fontSize(1.1.cssRem),
                    horizontalAlignment = Alignment.End,
                ) {
                    MenuItems()
                }
            }
        }
    }
}
